import React from 'react';
import Seo from '../components/Seo';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Award, Users, Globe, TrendingUp } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="About Us: UK Business Voice Provider"
        description="VOIP2 Limited is a London-based provider of VoIP telecommunications services, connecting businesses worldwide."
        path="/about"
        breadcrumbs={[{ name: 'About', path: '/about' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
            About VOIP2
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Leading global provider of VoIP telecommunications services, connecting businesses worldwide since 2010.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#0a1f2e' }}>
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                We believe communication should have no boundaries. Our mission is to provide businesses of all sizes with enterprise-grade VoIP solutions that are reliable, scalable, and affordable.
              </p>
              <p className="text-lg text-gray-600">
                Through continuous innovation and unwavering commitment to quality, we empower organizations to connect with customers and partners globally, without the limitations of traditional telecommunications infrastructure.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] rounded-lg p-6 sm:p-12 text-white">
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-3xl sm:text-5xl font-bold" style={{ color: '#c8ff00' }}>15+</div>
                  <div className="text-sm mt-2">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-5xl font-bold" style={{ color: '#c8ff00' }}>1000+</div>
                  <div className="text-sm mt-2">Enterprise Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-5xl font-bold" style={{ color: '#c8ff00' }}>90+</div>
                  <div className="text-sm mt-2">Countries Served</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-5xl font-bold" style={{ color: '#c8ff00' }}>99.99%</div>
                  <div className="text-sm mt-2">Uptime Guarantee</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulation */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-lg border border-gray-200 p-6 sm:p-8">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#0a1f2e' }}>Regulated in the UK</h2>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-600">
              <li><span className="font-semibold" style={{ color: '#0a1f2e' }}>Ofcom</span><br />Routing Identifier (RID) <span className="font-mono">SMW</span>, allocated by Ofcom</li>
              <li><span className="font-semibold" style={{ color: '#0a1f2e' }}>ICO</span><br />Data protection registration number ZC139352</li>
              <li><span className="font-semibold" style={{ color: '#0a1f2e' }}>Companies House</span><br />VOIP2 Limited, company number 16487711</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <Award className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Excellence</h3>
              <p className="text-gray-600">We strive for excellence in everything we do, from service quality to customer support.</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <Users className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Customer First</h3>
              <p className="text-gray-600">Our customers' success is our success. We're committed to their growth and satisfaction.</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <Globe className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Global Reach</h3>
              <p className="text-gray-600">We provide truly global services with local expertise and compliance in every market.</p>
            </Card>
            <Card className="p-8 text-center hover:shadow-xl transition-shadow">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-[#c8ff00]" />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#0a1f2e' }}>Innovation</h3>
              <p className="text-gray-600">Continuous innovation keeps us ahead, delivering cutting-edge solutions to our clients.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#0a1f2e' }}>
            Leadership Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                <Users className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ color: '#0a1f2e' }}>John Anderson</h3>
              <p className="text-gray-500 mb-3">CEO & Co-Founder</p>
              <p className="text-sm text-gray-600">15+ years in telecommunications, leading VOIP2's strategic vision and growth.</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                <Users className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ color: '#0a1f2e' }}>Sarah Martinez</h3>
              <p className="text-gray-500 mb-3">CTO</p>
              <p className="text-sm text-gray-600">Expert in VoIP technology and network infrastructure, driving our technical innovation.</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                <Users className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ color: '#0a1f2e' }}>Michael Chen</h3>
              <p className="text-gray-500 mb-3">VP of Operations</p>
              <p className="text-sm text-gray-600">Ensures operational excellence and oversees our global infrastructure.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] text-white">
            <h2 className="text-3xl font-bold mb-4">Join Us on Our Journey</h2>
            <p className="text-xl text-gray-300 mb-8">Be part of the future of global telecommunications</p>
            <Button 
              size="lg"
              style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
              className="hover:opacity-90 font-semibold"
              onClick={() => window.location.href = '/contact'}
            >
              Get in Touch
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;